import { getAlgoliaPersistentProductFilters } from '@libs/algolia';

import { MAX_CATEGORY_PATH_FILTERS } from '@/constants/algolia-filters';
import {
    buildCategoryPathFilter,
    CUSTOM_CATEGORY_NAMES_ATTRIBUTE,
    expandAlternateCategoryPaths,
    formatCategoryPathFilter,
    getCategoryFilterAttribute,
    resolveCategoryPathsForFilter,
    wrapOrFilterGroup,
} from '@/utils/category-path-utils';

/** Subcategory product-filter inputs shared by PLP search configuration. */
export type SubcatFiltering = {
    isEnabled?: boolean;
    /** Active descendant display paths under the matched parent. */
    allChildCategoryPaths?: string[];
    /** Active descendant paths under the matched parent only. */
    activeDescendantCategoryPaths?: string[];
    /** True when Query 3 answered, including zero hits. Empty children then means parent-only. */
    hasLiveDescendants?: boolean;
};

/** Sentinel filter used when subcat filtering is enabled but live descendants were not resolved. */
export const SUBCAT_NO_MATCH_FILTER = 'objectID:__plp_subcat_no_match__';

const buildActiveSubcatCategoryFilter = (
    activeDescendantCategoryPaths: string[],
    parentCategoryPaths: string[],
    attribute: string,
    displayPathCatalog: string[],
    reservedCategoryFilterCount: number
): string => {
    const categoryPaths = resolveCategoryPathsForFilter(
        [...expandAlternateCategoryPaths(activeDescendantCategoryPaths), ...parentCategoryPaths],
        displayPathCatalog
    );

    // Algolia accepts at most 1,000 combined filters per query. Keep room for
    // persistent filters and user refinements. Fall back to parent-only
    // assigned_category_paths (fail-closed) when the combined category filters
    // would exceed the reserved budget.
    if (categoryPaths.length + reservedCategoryFilterCount > MAX_CATEGORY_PATH_FILTERS) {
        return wrapOrFilterGroup(buildCategoryPathFilter(parentCategoryPaths, attribute, displayPathCatalog));
    }

    return wrapOrFilterGroup(formatCategoryPathFilter(categoryPaths, attribute));
};

const buildContentfulFilterParts = (filters: string[], preFilterLogic: string): string[] => {
    const contentfulFilters = filters.map(category => `${CUSTOM_CATEGORY_NAMES_ATTRIBUTE}:"${category}"`);

    if (preFilterLogic === 'OR') {
        return [wrapOrFilterGroup(contentfulFilters.join(' OR '))];
    }

    return contentfulFilters;
};

const buildUrlCategoryFilter = (algoliaCategoryPath: string, subcatFiltering: SubcatFiltering): string => {
    const {
        isEnabled: isSubcatFilteringEnabled = false,
        allChildCategoryPaths = [],
        activeDescendantCategoryPaths = [],
        hasLiveDescendants = false,
    } = subcatFiltering;

    const categoryFilterAttribute = getCategoryFilterAttribute(isSubcatFilteringEnabled);
    const currentCategoryPaths = expandAlternateCategoryPaths([algoliaCategoryPath]);
    const displayPathCatalog = [...new Set([...allChildCategoryPaths, ...currentCategoryPaths])];
    const customCategoryFilter = wrapOrFilterGroup(
        buildCategoryPathFilter(currentCategoryPaths, CUSTOM_CATEGORY_NAMES_ATTRIBUTE, displayPathCatalog)
    );
    const baseCategoryFilter = buildCategoryPathFilter(
        currentCategoryPaths,
        categoryFilterAttribute,
        displayPathCatalog
    );

    if (!isSubcatFilteringEnabled) {
        return customCategoryFilter;
    }

    const customCategoryFilterCount = resolveCategoryPathsForFilter(currentCategoryPaths, displayPathCatalog).length;
    let assignedCategoryFilter: string;

    if (activeDescendantCategoryPaths.length > 0) {
        assignedCategoryFilter = buildActiveSubcatCategoryFilter(
            activeDescendantCategoryPaths,
            currentCategoryPaths,
            categoryFilterAttribute,
            displayPathCatalog,
            customCategoryFilterCount
        );
    } else if (allChildCategoryPaths.length > 0 || hasLiveDescendants) {
        assignedCategoryFilter = wrapOrFilterGroup(baseCategoryFilter);
    } else {
        // assigned_category_paths stores direct CommerceTools assignments only (not parent
        // combinations). Parent-path inclusion already excludes products assigned solely to
        // inactive descendants; NOT exclusions would incorrectly hide products assigned to both
        // the current category and an inactive child.
        assignedCategoryFilter = SUBCAT_NO_MATCH_FILTER;
    }

    return `${customCategoryFilter} AND ${assignedCategoryFilter}`;
};

export const getConfigurationFilters = (
    filters: string[],
    preFilterLogic: string,
    algoliaCategoryPath: string,
    subcatFiltering: SubcatFiltering = {},
    onlineAt = Date.now()
): string => {
    const filterParts: string[] = [getAlgoliaPersistentProductFilters(onlineAt)];

    // pre-filters defined in Contentful will override category found in URL path.
    if (filters?.length > 0) {
        filterParts.push(...buildContentfulFilterParts(filters, preFilterLogic));
    } else if (algoliaCategoryPath !== '') {
        filterParts.push(buildUrlCategoryFilter(algoliaCategoryPath, subcatFiltering));
    }

    return filterParts.join(' AND ');
};
